// Parse an MT-originated KYB validation failure (HTTP 422 on
// `POST /payments/connection` / createLegalEntity) into a stable, field-mapped
// error the KYB form can render. Kept framework-free and pure so it is unit
// testable and reusable from the network layer.
//
// RESPONSE CONTRACT — the API returns HTTP 422 with a JSON body shaped like:
//   { "errors": [ { "field": <string|null>, "code": <string>, "message": <string> } ] }
// where `field` derives from MT's `parameter` (e.g. "documents" | "intended_use"
// | "expected_activity_volume" | "identifications"), `code` from MT's code
// (e.g. "parameter_invalid" | "parameter_missing"), and `message` is MT's human
// text. We also tolerate MT's raw shape verbatim — a top-level `error_list`
// array of `{ code, message, parameter }` plus a single `errors` object — so the
// parser survives whether the API forwards MT verbatim or reshapes it.

import axios from 'axios'

/** HTTP status MT/the API uses for a KYB validation rejection. */
export const KYB_VALIDATION_STATUS = 422

/** One normalized KYB field error, tolerant of both the API contract and the
 * raw MT shapes. `field` is MT's `parameter` (null when MT gave none). */
export interface KybApiFieldError {
  field: string | null
  code: string | null
  message: string
}

/** Thrown by the submit network fn on a 422 so the mutation's `onError` can
 * branch on it (surface field errors on the form) instead of the generic
 * error path. Extends `Error`, so it satisfies the mutation's `Error` channel. */
export class KybValidationError extends Error {
  readonly fieldErrors: KybApiFieldError[]
  readonly status = KYB_VALIDATION_STATUS

  constructor(fieldErrors: KybApiFieldError[]) {
    super('KYB validation failed')
    this.name = 'KybValidationError'
    this.fieldErrors = fieldErrors
    // Restore the prototype chain so `instanceof` works after transpilation.
    Object.setPrototypeOf(this, KybValidationError.prototype)
  }
}

interface RawKybError {
  field?: unknown
  parameter?: unknown
  code?: unknown
  message?: unknown
}

const toFieldError = (raw: unknown): KybApiFieldError | null => {
  if (!raw || typeof raw !== 'object') return null
  const entry = raw as RawKybError
  const message = typeof entry.message === 'string' ? entry.message.trim() : ''
  if (!message) return null
  // Prefer the API contract's `field`; fall back to MT's `parameter`.
  const fieldRaw = entry.field ?? entry.parameter
  const field = typeof fieldRaw === 'string' && fieldRaw ? fieldRaw : null
  const code = typeof entry.code === 'string' && entry.code ? entry.code : null
  return { field, code, message }
}

/**
 * Parse the JSON body of a 422 into normalized field errors, or `null` if the
 * body carries no recognizable validation errors. Reads MT's `error_list`
 * first, then the API-contract `errors` array, then tolerates a single `errors`
 * object.
 */
export const parseKybValidationErrorBody = (
  body: unknown,
): KybApiFieldError[] | null => {
  if (!body || typeof body !== 'object') return null
  const record = body as { errors?: unknown; error_list?: unknown }

  let candidates: unknown[] = []
  if (Array.isArray(record.error_list)) {
    candidates = record.error_list
  } else if (Array.isArray(record.errors)) {
    candidates = record.errors
  } else if (record.errors && typeof record.errors === 'object') {
    candidates = [record.errors]
  }

  const parsed = candidates
    .map(toFieldError)
    .filter((e): e is KybApiFieldError => e !== null)

  return parsed.length > 0 ? parsed : null
}

const isAxiosLike = (error: unknown): boolean =>
  axios.isAxiosError(error) ||
  (!!error &&
    typeof error === 'object' &&
    'isAxiosError' in error &&
    (error as { isAxiosError?: unknown }).isAxiosError === true)

/**
 * If `error` is an axios 422 whose body parses into KYB field errors, return a
 * `KybValidationError`; otherwise `null` (the caller then falls back to the
 * generic normalized error).
 */
export const extractKybValidationError = (
  error: unknown,
): KybValidationError | null => {
  if (!isAxiosLike(error)) return null
  const response = (error as { response?: { status?: number; data?: unknown } })
    .response
  if (!response || response.status !== KYB_VALIDATION_STATUS) return null
  const fieldErrors = parseKybValidationErrorBody(response.data)
  if (!fieldErrors) return null
  return new KybValidationError(fieldErrors)
}
